import React from 'react';
import clsx from 'clsx';

import Button from '../_custom/Button/Button';
import { SQUARE_END_ICON_STYLE } from '../_custom/Button/constants';
import { debtProdFilters, PURPLE_CROSS } from '../../assets/hostedassets';
import styles from './FilterTriggerButton.module.scss';

/**
 * The header filter trigger: a 2.5rem icon-only square (grey-tinted funnel) that, once a filter is
 * applied, expands into its purple active state with a clear (✕) affordance.
 *
 * Extracted from CompaniesList so screens sharing the filter drawer share the trigger too — the
 * same JSX previously lived verbatim in CompaniesList (Leads) and the Credit Bureau Report, and the
 * two could silently drift. DealsList, ManagementProfileTab, ManageUser and DebtProducts still
 * carry their own near-identical copies; migrate them here when next touched.
 *
 * The icon styles stay inline: they switch with the active state, and Button exposes no class-name
 * hooks for its icons — adding them touches every Button in the app, which is not this component's
 * call to make.
 */

const ICON_IDLE_STYLE: React.CSSProperties = {
    margin: '0 auto',
    display: 'block',
    filter: 'brightness(0) saturate(100%) invert(29%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%)',
};

const ICON_ACTIVE_STYLE: React.CSSProperties = {
    paddingRight: '0.5rem',
    borderRight: '1px solid var(--border-color)',
    width: '1.5rem',
};

type FilterTriggerButtonProps = {
    /** Drives the active (purple, with ✕) vs idle presentation. */
    hasActiveFilters: boolean;
    /** Open the filter drawer. Callers attach their own analytics here. */
    onOpen: () => void;
    /** The ✕ — clear all applied filters. Button stops propagation first, so this never re-opens. */
    onClear: () => void;
    idleLabel?: string;
    activeLabel?: string;
};

function FilterTriggerButton({ hasActiveFilters, onOpen, onClear, idleLabel = '', activeLabel = '' }: FilterTriggerButtonProps) {
    const label = hasActiveFilters ? activeLabel : idleLabel;
    const isLabelled = !!label;

    const iconStyles = isLabelled ? undefined : hasActiveFilters ? ICON_ACTIVE_STYLE : ICON_IDLE_STYLE;

    return (
        <Button
            text={label}
            aria-label={label || 'Filters'}
            onClick={onOpen}
            icon={debtProdFilters}
            className={clsx(styles.Trigger, {
                [styles.TriggerActive]: hasActiveFilters,
                [styles.TriggerLabelled]: isLabelled,
            })}
            variant={isLabelled ? 'purpleinverted' : 'secondary'}
            endIcon={hasActiveFilters ? PURPLE_CROSS : null}
            endIconStyles={SQUARE_END_ICON_STYLE}
            iconStyles={iconStyles}
            endIconClick={onClear}
        />
    );
}

export default FilterTriggerButton;
